// src/admin/pages/AdminManualApproval.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Row, Col, Card, Alert, Spinner, Badge, Form, Table, Button } from 'react-bootstrap';
import { ArrowLeft, CheckCircle, FileText, Info } from 'lucide-react';
import Swal from 'sweetalert2';
import { getPaymentLogDetails, approvePaymentLog } from '../../services/adminApi'; 

import './AdminPaymentsLog.css';

const AdminManualApproval = () => {
    const { logId } = useParams();
    const navigate = useNavigate();
    const [payload, setPayload] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Form States
    const [customPaymentId, setCustomPaymentId] = useState('');
    const [remark, setRemark] = useState('');

    const fetchDetails = async () => {
        try {
            // 🟢 adminApi फ़ंक्शन का उपयोग
            const response = await getPaymentLogDetails(logId);
            setPayload(response.data.data);
        } catch (err) {
            setError('Failed to fetch transaction details.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDetails();
    }, [logId]);

    const handleConfirmApprove = async (e) => {
        e.preventDefault();
        if (!customPaymentId.trim()) {
            Swal.fire({
                title: 'Required Field',
                text: 'Please enter a valid Payment ID or Transaction reference ID.',
                icon: 'warning',
                background: '#151521',
                color: '#ffffff',
                confirmButtonColor: '#dc3545'
            });
            return;
        }
        if (!remark.trim()) {
            Swal.fire({
                title: 'Required Field',
                text: 'Please provide an auditing reason for manual approval override.',
                icon: 'warning',
                background: '#151521',
                color: '#ffffff',
                confirmButtonColor: '#dc3545'
            });
            return;
        }

        setIsSubmitting(true);
        try {
            await approvePaymentLog(logId, {
                payment_id: customPaymentId,
                remark: remark
            });

            Swal.fire({
                title: 'Success!',
                text: 'Payment overrides applied successfully!',
                icon: 'success',
                background: '#151521',
                color: '#ffffff',
                confirmButtonColor: '#10B981'
            }).then(() => {
                navigate(`/admin/payment-logs/${logId}`);
            });
        } catch (err) {
            Swal.fire({
                title: 'Failed',
                text: err.response?.data?.message || 'Manual payment override failed.',
                icon: 'error',
                background: '#151521',
                color: '#ffffff',
                confirmButtonColor: '#dc3545'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading) return <div className="text-center p-5 admin-payments-container d-flex align-items-center justify-content-center"><Spinner animation="border" style={{ color: '#ffffff' }} /></div>;
    if (error) return <div className="container p-4 admin-payments-container"><Alert variant="danger">{error}</Alert></div>;
    if (!payload) return <div className="container p-4 admin-payments-container"><Alert variant="warning">Summary details not found.</Alert></div>;

    return (
        <div className="container-fluid py-4 admin-payments-container">
            {/* Header Area */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h2 className="admin-payments-title">Manual Payment Approval</h2>
                    <p className="admin-payments-subtitle">Review the payment logs details and manually mark status as 'SUCCESS'.</p>
                </div>
                <div>
                    <Link to={`/admin/payment-logs/${logId}`} className="btn btn-sm admin-payments-btn-secondary text-nowrap text-decoration-none">
                        <ArrowLeft size={16} /> Back to Log Details
                    </Link>
                </div>
            </div>

            <Row className="g-4">
                {/* Left Side */}
                <Col lg={8}>
                    <Card className="border-0 rounded-4 mb-4 admin-payments-card">
                        <Card.Body className="p-4">
                            <div className="d-flex align-items-center gap-2 pb-3 mb-4 admin-payments-border-bottom">
                                <FileText size={20} className="text-danger" />
                                <h5 className="fw-bold m-0 text-white">Transaction Summary</h5>
                                <Badge bg="warning" className="text-uppercase ms-auto text-dark">Pending Manual Overrides</Badge>
                            </div>
                            
                            <Table borderless className="mb-0 text-white" style={{ fontSize: '0.9rem' }}>
                                <tbody>
                                    <tr className="admin-payments-border-bottom">
                                        <td className="admin-payments-table-text-muted py-2 ps-0" style={{ width: '35%' }}>Current Status</td>
                                        <td><Badge bg="warning" className="text-uppercase text-dark">{payload.payment_status}</Badge></td>
                                    </tr>
                                    <tr className="admin-payments-border-bottom">
                                        <td className="admin-payments-table-text-muted py-2 ps-0">Application ID</td>
                                        <td className="fw-semibold text-white">{payload.application_id || 'N/A'}</td>
                                    </tr>
                                    <tr className="admin-payments-border-bottom">
                                        <td className="admin-payments-table-text-muted py-2 ps-0">Order ID</td>
                                        <td className="font-monospace text-secondary">{payload.order_id || 'N/A'}</td>
                                    </tr>
                                    <tr className="admin-payments-border-bottom">
                                        <td className="admin-payments-table-text-muted py-2 ps-0">Original Payment ID</td>
                                        <td className="font-monospace text-secondary">{payload.payment_id || 'N/A'}</td>
                                    </tr>
                                    <tr className="admin-payments-border-bottom">
                                        <td className="admin-payments-table-text-muted py-2 ps-0">User Name</td>
                                        <td className="fw-semibold text-white">{payload.user?.name || payload.user_name}</td>
                                    </tr>
                                    <tr className="admin-payments-border-bottom">
                                        <td className="admin-payments-table-text-muted py-2 ps-0">User Email</td>
                                        <td className="admin-payments-table-text-muted text-truncate" style={{ maxWidth: '300px' }}>{payload.user?.email || payload.email}</td>
                                    </tr>
                                    <tr className="admin-payments-border-bottom">
                                        <td className="admin-payments-table-text-muted py-2 ps-0">Amount</td>
                                        <td className="fw-bold text-white">₹{Number(payload.amount).toFixed(2)}</td>
                                    </tr>
                                    <tr className="admin-payments-border-bottom">
                                        <td className="admin-payments-table-text-muted py-2 ps-0">Gateway Provider</td>
                                        <td className="text-uppercase text-secondary">{payload.gateway_provider || '-'}</td>
                                    </tr>
                                    <tr>
                                        <td className="admin-payments-table-text-muted py-2 ps-0">Original Log Date</td>
                                        <td className="admin-payments-table-text-muted">{new Date(payload.created_at).toLocaleString('en-IN')}</td>
                                    </tr>
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Right Side */}
                <Col lg={4}>
                    <Card className="border-0 rounded-4 mb-4 admin-payments-card">
                        <Card.Body className="p-4">
                            <div className="d-flex align-items-center gap-2 pb-3 mb-4 admin-payments-border-bottom">
                                <CheckCircle size={20} className="text-success" />
                                <h5 className="fw-bold m-0 text-white">Approval Details</h5>
                            </div>
                            
                            <Alert className="d-flex align-items-start gap-2 border-0 py-3 rounded-3 admin-payments-alert-info">
                                <Info size={18} className="flex-shrink-0 mt-0.5" />
                                <div>
                                    <strong className="text-white d-block mb-1">Important Note</strong>
                                    You are about to force-update this record's billing status to Success. Ensure you have verified bank statements.
                                </div>
                            </Alert>

                            <Form onSubmit={handleConfirmApprove}>
                                <Form.Group className="mb-3">
                                    <Form.Label className="text-white fw-semibold small">Verified Payment ID <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Enter Gateway / Bank Payment ID"
                                        className="py-2 rounded-3 text-white admin-payments-input admin-payments-input-group px-3"
                                        value={customPaymentId}
                                        onChange={(e) => setCustomPaymentId(e.target.value)}
                                        required
                                    />
                                    <Form.Text className="admin-payments-table-text-muted" style={{ fontSize: '0.75rem' }}>
                                        Enter transaction trace ID or reference ID.
                                    </Form.Text>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label className="text-white fw-semibold small">Audit Remarks / Reason <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={4}
                                        placeholder="e.g., Funds received in Merchant Bank Account (Ref: TXN-XXXXX)."
                                        className="rounded-3 text-white admin-payments-input admin-payments-input-group px-3 py-2"
                                        value={remark}
                                        onChange={(e) => setRemark(e.target.value)}
                                        maxLength={500}
                                        required
                                    />
                                    <Form.Text className="admin-payments-table-text-muted d-block text-end" style={{ fontSize: '0.75rem' }}>
                                        {remark.length}/500
                                    </Form.Text>
                                </Form.Group>

                                <Alert className="border-0 rounded-3 d-flex gap-2 admin-payments-alert-warning">
                                    <span>⚠️</span>
                                    <div>
                                        <strong className="text-white d-block">Warning</strong>
                                        This override updates database values immediately. This action cannot be undone.
                                    </div>
                                </Alert>

                                <div className="d-flex gap-2 mt-4">
                                    <Button variant="outline-secondary" className="w-50 py-2 border rounded-3 bg-transparent text-secondary admin-payments-btn-secondary text-nowrap" onClick={() => navigate(`/admin/payment-logs/${logId}`)} style={{ borderColor: '#2D3142' }}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" className="w-50 py-2 rounded-3 text-white border-0 admin-payments-btn-success text-nowrap" disabled={isSubmitting}>
                                        {isSubmitting ? <Spinner size="sm" className="me-1" /> : 'Confirm & Approve'}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default AdminManualApproval;